import React, { useMemo, useState } from 'react';
import { ActivityHeatmapResponse } from 'types/interfaces/activity';
import { buildHeatRow, captionClause, cellTooltip, HeatCell } from './heatScore';

// The heat strip tile (Asana 1218957945501621, Heat Strip Rubric): two rows of
// HEAT_WEEKS cells, oldest left, the current ISO week outlined. Hue and step
// come from heatScore.ts; this file only paints. Cells carry data attributes
// the evidence reads.

const HUE_COLOR = { purple: '#7B61FF', pink: '#E255A1' } as const;

interface RowProps {
  label: string;
  cells: HeatCell[];
  rowKey: 'community' | 'you';
  activeKey: string | null;
  onActive: (key: string | null) => void;
}

const Row: React.FC<RowProps> = ({ label, cells, rowKey, activeKey, onActive }) => (
  <div className="home-heat__row" data-testid={`heat-row-${rowKey}`}>
    <span className="home-heat__row-label">{label}</span>
    <div className="home-heat__cells">
      {cells.map((cell) => {
        const key = `${rowKey}:${cell.isoWeek}`;
        const style: React.CSSProperties =
          cell.kind === 'filled'
            ? { background: HUE_COLOR[cell.hue], opacity: cell.alpha }
            : cell.kind === 'empty'
              ? { background: 'rgba(255,255,255,0.06)' }
              : { background: 'transparent', border: '1px dashed rgba(255,255,255,0.14)' };
        return (
          <span
            key={cell.isoWeek}
            className={`home-heat__cell${cell.isCurrent ? ' home-heat__cell--current' : ''}${activeKey === key ? ' is-active' : ''}`}
            data-hue={cell.kind === 'filled' ? cell.hue : undefined}
            data-step={cell.step}
            data-kind={cell.kind}
            data-current={cell.isCurrent || undefined}
            data-week={cell.isoWeek}
            title={cellTooltip(cell)}
            tabIndex={0}
            role="img"
            aria-label={cellTooltip(cell)}
            onMouseEnter={() => onActive(key)}
            onMouseLeave={() => onActive(null)}
            onFocus={() => onActive(key)}
            onBlur={() => onActive(null)}
            onClick={() => onActive(activeKey === key ? null : key)}
          >
            <span className="home-heat__cell-fill" style={style} />
          </span>
        );
      })}
    </div>
  </div>
);

interface Props {
  data: ActivityHeatmapResponse | undefined;
  isLoading: boolean;
}

const HeatStrip: React.FC<Props> = ({ data, isLoading }) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const rows = useMemo(() => {
    if (!data) return null;
    const options = { joinedIsoWeek: data.joinedIsoWeek, currentIsoWeek: data.currentIsoWeek };
    return {
      community: buildHeatRow(data.community, { ...options, isYou: false }),
      you: buildHeatRow(data.you, { ...options, isYou: true }),
    };
  }, [data]);
  const clause = rows && data ? captionClause(rows.you, data.joinedIsoWeek, data.currentIsoWeek) : null;
  const active = useMemo(() => {
    if (!rows || !activeKey) return null;
    const [row, week] = activeKey.split(':');
    return rows[row as 'community' | 'you'].find((c) => c.isoWeek === week) ?? null;
  }, [rows, activeKey]);

  return (
    <div className="home-pulse__tile home-pulse__tile--heat home-heat" data-testid="heat-strip" aria-busy={isLoading || undefined}>
      {rows ? (
        <>
          <Row label="Community" cells={rows.community} rowKey="community" activeKey={activeKey} onActive={setActiveKey} />
          <Row label="You" cells={rows.you} rowKey="you" activeKey={activeKey} onActive={setActiveKey} />
          <p className="home-heat__caption" data-testid="heat-caption">
            {active ? cellTooltip(active) : `Last 12 weeks · this week outlined${clause ? ` · ${clause}` : ''}`}
          </p>
        </>
      ) : (
        <p className="home-heat__caption">{isLoading ? 'Loading the last 12 weeks…' : 'Activity history is not available yet.'}</p>
      )}
    </div>
  );
};

export default HeatStrip;
